// Clavian Boot's copy in the venue console (the `boot` and `addons` branches of the `console` namespace) — the PCs & games
// section (its Boot server, Windows images and Games pages), the PCs' boot state on the Floor and in the station window, and
// the organisation's read-only add-ons page.
//
// The voice is BRAND.md §6's: say what happens, plainly. The words are the glossary's: station, boot server, Windows image,
// game disk, version. What staff do has plain names — Change Windows using a PC, Update games, Save and roll out, Throw
// away — and the internal terms (edit boot, server edit, publish, discard, write-back, layer, chain, LUN) stay out of every
// everyday screen; write-back is named only inside an Advanced section. Nothing here names a spec section, a feature id, a
// decision record or an internal flag.
export const bootCopy = {
    title: 'PCs & games',
    intro: 'Start your PCs from the boot server: one Windows for every PC, with their games, updated in one place.',
    loading: 'Loading PCs & games',
    unavailable: 'PCs & games could not be loaded.',
    forbidden: 'Your role cannot open PCs & games.',
    readOnly: 'You can look at this page but not change it. Ask an owner for access.',
    notOnPlan: {
        title: 'Clavian Boot isn’t on your plan',
        body: 'Clavian Boot is an add-on, priced per PC each month. To start your PCs from a boot server, ask Clavian to add it to your organisation. Your owner will see it under Add-ons once it is on.',
    },
    // The section's three pages, shown as tabs under its header.
    pages: {
        label: 'PCs & games pages',
        server: 'Boot server',
        images: 'Windows images',
        games: 'Games',
    },
    // -- Servers -------------------------------------------------------------------------------------
    servers: {
        empty: {
            title: 'Pair your boot server',
            body: 'Install Clavian Boot on the computer that will serve your PCs, then get a pairing code here and type it into the Pairing box on the server’s status page.',
        },
        waiting: '“{{name}}” is waiting to be paired. Codes last 30 minutes.',
        // The status card's one sentence.
        summary: {
            online: 'Your boot server is online and ready to start your PCs.',
            onlineBooted_one: 'Your boot server is online. {{count}} PC is started from it now.',
            onlineBooted_other: 'Your boot server is online. {{count}} PCs are started from it now.',
            licenceGrace: 'Your boot server is online, but its licence needs renewing soon.',
            licenceExpired: 'Your boot server is online, but its licence has expired.',
            offline: 'Your boot server is not reporting. It last reported {{when}}.',
            pairing: 'Your boot server is waiting to be paired.',
            none: 'No boot server is paired yet.',
        },
        pair: 'Get a pairing code',
        pairAgain: 'New pairing code',
        rename: 'Rename',
        unpair: 'Unpair',
        status: 'Status',
        statuses: { online: 'Online', offline: 'Not reporting', pairing: 'Waiting to be paired' },
        version: 'Version',
        lastSeen: 'Last heard from',
        pairedOn: 'Paired',
        ramCache: 'Memory cache',
        unknown: 'Not reported yet',
        never: 'never',
        offlineNote: 'PCs it already knows keep starting from its last settings. Check it is switched on and connected.',
        licence: {
            label: 'Licence',
            states: { valid: 'Valid', grace: 'Renew soon', expired: 'Expired' },
            graceTitle: 'The boot server’s licence needs renewing',
            graceBody: 'It could not renew in time. PCs it already knows keep booting, but a new PC will be refused until it is renewed. Check that the boot server can reach the internet.',
            expiredTitle: 'The boot server’s licence has expired',
            expiredBody: 'Only the PCs included free can boot. Get the boot server back online to renew it.',
            okNote: 'The boot server renews its licence by itself whenever it is online.',
        },
        disks: {
            title: 'Drives',
            intro: 'What each of the boot server’s drives holds, and how much room is left.',
            none: 'The boot server has not reported its drives yet.',
            role: 'Holds',
            path: 'Location',
            use: 'In use',
            useLabel: '{{role}} drive in use',
            free: 'Free',
            total: 'Size',
            low: 'Running low',
            roles: { system: 'The server itself', images: 'Images', games: 'Game disks', writeback: 'Changes during play', shader: 'Shader caches' },
        },
        pairing: {
            title: 'Pair the boot server',
            intro: 'You get a code to type into the boot server. It works once, for 30 minutes.',
            codeIntro: 'Type this code into the boot server.',
            name: 'Name',
            nameHint: 'What staff call it. Leave empty to keep the current name.',
            get: 'Get the code',
            codeLabel: 'Pairing code',
            step1: 'On the boot server, open Start menu > Clavian > Clavian Boot status.',
            step2: 'Find the Pairing box, and type the code above into it.',
            step3: 'Press Pair. This page updates when the server connects.',
            expires: 'The code stops working at {{time}}.',
            done: 'Done',
            failed: 'The pairing code could not be made. Try again.',
        },
        renameDialog: {
            title: 'Rename the boot server',
            name: 'Name',
            required: 'Give the boot server a name.',
            saved: 'Boot server renamed',
            failed: 'The boot server could not be renamed. Try again.',
        },
        unpairDialog: {
            title: 'Unpair {{name}}?',
            body: 'It stops taking instructions from your venue at once. PCs it already knows keep booting from its last settings, but you cannot change anything until you pair it again.',
            keep: 'Keep it paired',
            confirm: 'Unpair',
            done: 'Boot server unpaired',
            failed: 'The boot server could not be unpaired. Try again.',
        },
        update: {
            title: 'Software',
            intro: 'The boot server’s own software. It downloads and checks a new version by itself, but installs it only when you choose Update now.',
            notReported: 'The boot server has not reported its software version yet.',
            installed: 'Installed version',
            state: 'Updates',
            states: {
                none: 'Up to date',
                downloading: 'Downloading an update',
                available: 'Update available',
                installing: 'Installing',
                installed: 'Up to date',
                failed: 'Update failed',
                rolled_back: 'Update rolled back',
            },
            available: 'Update available: {{version}}',
            availableBody: 'It is downloaded and checked. Nothing changes until you choose Update now.',
            installingBody: 'Installing {{version}}. The boot service restarts, which takes a minute or two; this page updates when it is back.',
            bootedPcs: 'PCs booted now',
            last: 'Last update',
            never: 'None yet',
            lastResult: {
                installed: '{{from}} to {{to}}: installed {{when}}',
                failed: '{{from}} to {{to}}: failed {{when}}',
                rolled_back: '{{from}} to {{to}}: rolled back {{when}}',
            },
            action: 'Update now',
            offline: 'The boot server is not reporting. Update now needs it online.',
            dialog: {
                title: 'Update the boot server to {{version}}?',
                body: 'The boot service stops, installs the new version and starts again, which takes a minute or two. Its settings, images and pairing are kept. If the new version does not start, the previous one is put back.',
                bootedTitle_one: '{{count}} PC is booted from this server',
                bootedTitle_other: '{{count}} PCs are booted from this server',
                bootedBody: 'Shut them down first, or update anyway. Windows usually rides out the short restart and their changes are kept, but a PC can freeze or restart if it takes too long.',
                confirm: 'Update now',
                confirmAnyway: 'Update anyway',
                sent: 'The update was sent to the boot server',
                failed: 'The update could not be sent. Try again.',
            },
        },
    },
    // -- Windows images ------------------------------------------------------------------------------
    images: {
        title: 'Windows images',
        toolbar: 'Windows image actions',
        unavailable: 'The Windows images could not be loaded.',
        new: 'Capture Windows from a PC',
        import: 'Import',
        default: 'Venue default',
        versionNumber: 'Version {{number}}',
        kinds: { system: 'Windows image', game: 'Game disk' },
        driveLetterBadge: 'Drive {{letter}}:',
        defaultSet: '{{name}} is now the Windows new PCs start',
        basedOn: 'Saved from {{name}} · version {{number}}',
        // One card per Windows image: which classes start it and how many PCs that is.
        card: {
            version: 'Version {{number}}',
            versionWhen: 'Version {{number}} · {{when}}',
            noVersion: 'Not ready yet',
            current: 'Current version',
            classesLabel: 'Used by classes',
            pcsLabel: 'PCs',
            noClasses: 'No class chooses it',
            pcsStart_one: '{{count}} PC starts it',
            pcsStart_other: '{{count}} PCs start it',
            noPcs: 'No PC starts it yet',
            versions: 'Versions',
        },
        status: {
            ready: 'Ready',
            preparing: 'Being made',
            merging: 'Merging old versions',
            makingIndependent: 'Being made independent',
            editing: 'Windows being changed',
            editingOn: 'Windows being changed on {{pc}}',
            serverEditOpening: 'Opening on the boot server as {{letter}}:',
            serverEditOpen: 'Open for updates on {{letter}}:',
            failed: 'Failed',
            waitingForCapture: 'Waiting for the PC',
            capturing: 'Capturing',
            importing: 'Importing',
            preparingJob: 'Preparing',
            withPercent: '{{status}} {{percent}}%',
            withDetail: '{{status}} · {{detail}}',
            bytes: '{{done}} of {{total}} {{unit}}',
            open: 'Show how far {{name}} has got',
        },
        empty: {
            title: 'No Windows image yet',
            body: 'A Windows image is the Windows your PCs start from. Capture one from a PC you have set up the way you want, or import one you already have.',
        },
        actions: {
            versions: 'Versions and restore points',
            change: 'Change Windows using a PC',
            updateGames: 'Update games',
            driveLetter: 'Drive letter',
            save: 'Save and roll out',
            discard: 'Throw away',
            nic: 'Network drivers',
            makeDefault: 'Make the venue default',
            makeIndependent: 'Make it independent',
            archive: 'Archive',
            named: '{{action}}, {{name}}',
            failed: 'That could not be done. Try again.',
        },
        // An image saved from another stands on one of its versions until it is made independent.
        dependency: {
            title: 'Another Windows image is built on this one',
            merge: 'The Windows image “{{name}}” is built on version {{number}} of this one, so that version cannot be merged away yet.',
            archive: 'The Windows image “{{name}}” is built on version {{number}} of this one, so this one cannot be archived yet.',
            action: 'Make {{name}} independent',
        },
        independent: {
            title: 'Make {{name}} independent?',
            body: '{{name}} was saved from {{from}}. Its first version is copied into a base of its own, so it no longer needs {{from}}.',
            off: 'Every PC that starts {{name}} must be switched off while this runs. If any are on, the boot server says how many, and you can try again.',
            confirm: 'Make it independent',
            started: '{{name}} is being made independent',
            failed: 'It could not be started. Try again.',
        },
        wizard: {
            title: 'Capture Windows from a PC',
            importTitle: 'Import a Windows image',
            importGamesTitle: 'Import a game disk',
            gamesTitle: 'Add games from a folder',
            intro: 'Make the Windows your PCs start from.',
            introGames: 'Make a game disk that every PC sees as a second drive.',
            steps: { name: 'Name', capture: 'Capture', preparing: 'Preparing', importing: 'Importing', copying: 'Files', report: 'Report' },
            name: 'Name',
            nameHint: 'For example “Windows 11 Gaming”.',
            nameHintGames: 'For example “Game library”.',
            nameRequired: 'Give it a name.',
            path: 'File on the boot server',
            pathHint: 'The full path of a VHD, VHDX or VMDK file on the boot server, for example D:\\Imports\\gaming.vhdx.',
            pathRequired: 'Write where the file is on the boot server.',
            how: 'How to make it',
            howOptions: { capture: 'Capture from a PC', folder: 'From a folder on the boot server' },
            folder: 'Folder on the boot server',
            folderHint: 'A drive or folder on the boot server that holds your games, for example D:\\Games, or the root of a game disk you have attached there.',
            folderMode: 'What happens to the folder',
            folderModes: {
                move: 'Move the games out of the folder (frees the space)',
                moveHint: 'Recommended. Each file is copied in, checked, then removed from the folder, so the games are never on the drive twice. A file that does not check out stays in the folder.',
                copy: 'Copy the games (keeps the folder)',
                copyHint: 'The folder stays as it is. The drive needs room for the games twice.',
            },
            folderRequired: 'Write which folder on the boot server to use.',
            startFolder: 'Add the games',
            makeDefault: 'PCs without a Windows image of their own start this one',
            makeDefaultReplace: 'Make this the Windows PCs without one of their own start (replaces the current one)',
            next: 'Get the capture code',
            startImport: 'Start the import',
            failed: 'It could not be made. Try again.',
            codeLabel: 'Capture code',
            capture: {
                step1: 'On the PC you have set up the way you want every PC to be, open this address in a browser. Put your boot server’s address in place of the brackets; it is on the boot server’s status page, in the “Capture a reference PC” card.',
                step2: 'Choose Download Clavian Boot Capture and open it. Windows asks if it may make changes: choose Yes.',
                step3: 'The app finds your boot server by itself. When it asks, type the capture code shown above.',
                step4: 'Leave the PC alone until the app says it is done. This page follows its progress.',
                requirements: 'The PC must start with UEFI, have a GPT disk, run Windows 10 22H2 or Windows 11, and have BitLocker off. It must not be the boot server itself. The code works once.',
                advanced: 'Advanced: command line',
                advancedHint: 'Instead of the app, copy the folder components\\Clavian.Boot.Client from the boot server’s install folder (C:\\Program Files\\Clavian\\Boot) onto the PC, open a command prompt as administrator there, and run this command with the boot server’s address in place of the brackets:',
            },
            copy: 'Copy the command',
            copyLink: 'Copy the address',
            copied: 'Copied',
            started: 'I have started the capture',
            progress: {
                capturing: 'Copying the disk from the PC',
                preparing: 'Preparing Windows to start over the network',
                importing: 'Importing and preparing the image',
                copying: 'Putting the games into the new game disk',
                failed: 'It did not finish',
                failedTitle: 'It could not be made',
                failedBody: 'The boot server reported a problem. Close this, fix what it names and start again.',
                keepOpen: 'You can close this window. It keeps being made and shows up in the list when it is ready.',
            },
            progressTitle: 'Making {{name}}',
            progressIntro: 'How far it has got. Closing this does not stop it.',
            runInBackground: 'Close and let it run',
            close: 'Close',
            publishedTitle: '{{name}} is ready',
            publishedBody: 'Version 1 is saved ({{size}}). PCs get it the next time they start.',
            done: 'Done',
            guardTitle: 'Close without finishing?',
            guardBody: 'The capture code is shown only once. If you close this, you will need to start again to get a new one.',
            guardKeep: 'Keep it open',
            guardLeave: 'Close anyway',
        },
        versions: {
            title: '{{name}}: versions',
            intro: 'Every ready version is a restore point. Restoring makes an earlier version the one PCs start next time.',
            label: 'Versions of {{name}}',
            none: 'No versions yet.',
            failed: 'The versions could not be loaded.',
            number: 'Version {{number}}',
            current: 'In use',
            restorePoint: 'Restore point',
            viewReport: 'Preparation report',
            restore: 'Restore',
            prepareAgain: 'Prepare again',
            prepareAgainDone: 'Version {{number}} is being prepared again.',
            prepareAgainFailed: 'The version could not be prepared again.',
            merge: 'Merge old versions',
            mergePending: 'The boot server has been asked to merge old versions. They disappear from this list when it is done.',
            // A game disk made from a folder that did not finish: the way to carry on.
            moveRest: 'Move the rest',
            tryAgain: 'Try again',
            moveRestDone: 'The boot server is moving what is still in the folder. No restart is needed.',
            tryAgainDone: 'The boot server is copying the games from the folder again.',
            moveRestFailed: 'It could not be started. Try again.',
            states: {
                published: 'Ready',
                capturing: 'Being captured',
                preparing: 'Being prepared',
                editing: 'Being changed',
                failed: 'Failed',
                discarded: 'Thrown away',
                merged: 'Merged',
            },
        },
        prepareFromPublished: {
            title: 'Prepare version {{number}} again?',
            body: 'This makes a new version of {{name}} from version {{number}} and prepares it again. Version {{number}} is not changed and stays as a restore point. The new version is ready for PCs when its preparation passes.',
            confirm: 'Make a new version',
            done: 'Version {{number}} is being made from version {{from}}.',
            failed: 'The new version could not be made. Try again.',
        },
        restore: {
            title: 'Restore version {{number}}?',
            body: 'PCs start version {{number}} of {{name}} from their next restart. The versions after it stay as restore points.',
            confirm: 'Restore',
            failed: 'The version could not be restored. Try again.',
        },
        merge: {
            title: 'Merge old versions of {{name}}',
            intro: 'Merging joins old versions into one new base to save space. The restore points it joins are gone for good.',
            upTo: 'Merge everything older than',
            option: 'Version {{number}}',
            removesLabel: 'Restore points that will be removed',
            removes_one: 'This removes {{count}} restore point:',
            removes_other: 'This removes {{count}} restore points:',
            removed: 'Version {{number}} {{note}}',
            keeps: 'Version {{number}} stays, as the new base.',
            confirm_one: 'Merge and remove {{count}} restore point',
            confirm_other: 'Merge and remove {{count}} restore points',
            failed: 'The merge could not be started. Try again.',
        },
        // Change Windows using a PC.
        change: {
            title: 'Change {{name}} using a PC',
            intro: 'One PC starts this Windows so you can install or update things on it. Other PCs keep the current version until you save and roll out the change.',
            pc: 'PC to use',
            pcHint: 'Only PCs nobody is playing on are listed.',
            pcPlaceholder: 'Choose a PC',
            choose: 'Choose a PC to use.',
            noIdle: 'Every PC is busy. Try again when one is free.',
            start: 'Start changing Windows',
            started: 'Windows can now be changed on {{name}}. Restart that PC to start into it.',
            failed: 'It could not be started. Try again.',
        },
        // Update games: a game disk opens on the boot server.
        updateGames: {
            title: 'Update the games on {{name}}?',
            body: 'A new version of {{name}} opens on drive {{letter}}: on the boot server. Install and update games there with the game launchers on the server (Steam, Riot Client, Epic Games, Battle.net), their libraries on {{letter}}:.',
            pcs: 'PCs keep playing the current games while it is open. When you save and roll out, they get the new games the next time they start.',
            letter: 'Nothing else on the boot server may use {{letter}}: while it is open. If something does, the boot server says so and nothing is opened.',
            confirm: 'Open it on the boot server',
            started: 'The games on {{name}} are opening on {{letter}}: on the boot server',
            failed: 'It could not be opened. Try again.',
            refused: 'The games on {{name}} could not be opened for updating',
        },
        // Save and roll out: the change made on a PC, or the games updated on the boot server.
        save: {
            title: 'Save the changes made on {{pc}}',
            titleImage: 'Save the changes made to {{name}}',
            titleServer: 'Save the games updated on the boot server',
            intro: 'The PC shuts down, the changes become a new version, and PCs get it the next time they start.',
            introServer: 'Close the game launchers on the boot server first. The drive is taken off the server, the changes become a new version, and PCs get it the next time they start.',
            as: 'Save it as',
            asVersion: 'A new version of {{name}}',
            asVersionHint: 'Every PC that starts {{name}} gets it at its next restart.',
            asImage: 'A new image',
            asImageHint: '{{name}} stays as it is. Give one class the new image to try it first.',
            newName: 'Name of the new image',
            newNameHint: 'For example “Windows 11 Tournament”.',
            newNameRequired: 'Give the new image a name.',
            confirmNew: 'Save as a new image',
            doneNew: '{{name}} is being saved as a new image',
            note: 'What changed',
            noteHint: 'A short note shown beside the version, for example “Updated Valorant”.',
            confirm: 'Save and roll out',
            done: 'The changes to {{name}} are being saved. PCs get them the next time they start.',
            failed: 'The changes could not be saved. Try again.',
        },
        discard: {
            title: 'Throw away the changes made on {{pc}}?',
            titleImage: 'Throw away the changes made to {{name}}?',
            titleServer: 'Throw away the games updated on the boot server?',
            body: 'Everything changed since is thrown away. The Windows image stays as it was.',
            bodyServer: 'The drive is taken off the boot server, and everything installed or updated on it since it was opened is thrown away. The game disk stays as it was.',
            keep: 'Keep the changes',
            confirm: 'Throw away',
            failed: 'The changes could not be thrown away. Try again.',
        },
        nic: {
            title: 'Network drivers for {{name}}',
            intro: 'A PC needs its network driver inside Windows to start from the network at all.',
            mode: 'Which drivers',
            modes: { auto: 'Seen at your venue', families: 'Ones I choose', broad: 'All of them' },
            hint: {
                auto: 'Uses the network cards your PCs have reported so far. The safe choice.',
                families: 'Uses only the driver families you list. A PC with a different card will not start.',
                broad: 'Includes every driver Clavian supports. A bigger image, for venues with mixed or new hardware.',
            },
            families: 'Driver families',
            familiesHint: 'Separate them with commas, for example intel-i225, realtek-8125.',
            familiesRequired: 'List at least one driver family.',
            nextVersion: 'The choice applies to the next version you save.',
            saved: 'Network drivers saved',
            failed: 'The choice could not be saved. Try again.',
        },
        driveLetter: {
            title: 'Drive letter for {{name}}',
            intro: 'The letter this game disk has on every PC and on the boot server. Game launchers find their libraries by it, so change it only when something else needs the letter.',
            letter: 'Drive letter',
            hint: 'Letters other game disks use are not offered.',
            nextBoot: 'PCs use the new letter from their next start. Point the launchers’ libraries at it in Windows and on the boot server.',
            openOnServer: 'The game disk is open for updates on the boot server. Save and roll out, or throw away, those changes first.',
            saved: 'Drive letter saved',
            failed: 'The drive letter could not be saved. Try again.',
        },
        archive: {
            title: 'Archive {{name}}?',
            body: 'PCs using it fall back to the venue default Windows. You can still see its versions.',
            usedByClasses_one: 'The class {{names}} starts this Windows image. Choose another Windows image for it first, under Stations, Classes.',
            usedByClasses_other: 'The classes {{names}} start this Windows image. Choose another Windows image for them first, under Stations, Classes.',
            confirm: 'Archive',
            failed: 'It could not be archived. Try again.',
        },
    },
    // -- Games ---------------------------------------------------------------------------------------
    games: {
        title: 'Games',
        toolbar: 'Game actions',
        unavailable: 'The game disks could not be loaded.',
        fromFolder: 'Add games from a folder',
        import: 'Import',
        empty: {
            title: 'No games yet',
            body: 'A game disk holds the games every PC sees as a second drive, so they are installed once. Add your games from a folder on the boot server.',
        },
        card: {
            size: 'Size',
            updated: 'Last updated',
            goesWith: 'Goes with',
            goesWithAll: 'Every Windows image',
            noVersion: 'Not ready yet',
        },
        failed: {
            title: 'Adding the games did not finish',
            bodyMove: 'What was moved is already in the game disk. The rest is still in the folder. Move the rest to carry on where it stopped.',
            bodyCopy: 'The folder is as it was. Try again to copy the games once more.',
        },
    },
    report: {
        title: 'Preparation report — version {{number}}',
        intro: 'What was done to make this version boot over the network.',
        label: 'Preparation steps',
        none: 'There is no report for this version.',
        noChanges: 'Nothing needed changing.',
        status: { ok: 'Done', failed: 'Failed', skipped: 'Skipped', unknown: 'Not reported' },
    },
    // -- PCs -----------------------------------------------------------------------------------------
    pcs: {
        title: 'Boot PCs',
        toolbar: 'PC actions',
        unavailable: 'The PCs could not be loaded.',
        stationNumber: 'Station {{number}}',
        count_one: '{{count}} PC · {{behind}} behind',
        count_other: '{{count}} PCs · {{behind}} behind',
        restartBehind_one: 'Restart {{count}} PC that is behind',
        restartBehind_other: 'Restart {{count}} PCs that are behind',
        empty: {
            title: 'No PCs have booted yet',
            body: 'Set a PC to start from the network. It shows up here the first time it boots, and is added with the venue default image.',
        },
        columns: {
            station: 'Station',
            mac: 'Network address',
            image: 'Image and version',
            writeback: 'Changes kept on',
            secureBoot: 'Secure Boot',
            gpu: 'Graphics',
            lastBoot: 'Last boot',
        },
        notLinked: 'Not linked',
        linked: 'Linked station',
        noImage: 'No image',
        assigned: 'Chosen',
        behind: 'Behind · current is {{number}}',
        override: 'Overridden',
        writebackWhere: { local: 'The PC’s own drive', server: 'The boot server' },
        unknown: 'Not reported',
        on: 'On',
        off: 'Off',
        neverBooted: 'Not booted yet',
        actions: {
            assign: 'Station and image',
            writeback: 'Where it keeps changes',
            restart: 'Restart if idle',
            wipeWriteback: 'Wipe its changes',
            wipeShader: 'Wipe shader cache',
            shaderSeed: 'Share its shader cache',
        },
        writebackDialog: {
            title: 'Where this PC keeps its changes',
            intro: 'While a PC runs, everything it writes is kept somewhere until its next boot. Set this PC apart from the venue setting.',
            policy: 'Keep changes',
            inherit: 'Follow the venue setting',
            nextBoot: 'It applies from the PC’s next boot.',
            saved: 'Saved',
            failed: 'The choice could not be saved. Try again.',
        },
        wipe: {
            title: { writeback: 'Wipe changes', shaderCache: 'Wipe shader cache' },
            intro: {
                writeback: 'Throws away everything this PC has changed since it booted. A PC that is running restarts onto a clean disk.',
                shaderCache: 'Resets the graphics shader cache from a fresh copy. Do this after a graphics driver problem.',
            },
            scope: 'Which PCs',
            scopes: { pc: 'This PC', family: 'Same graphics ({{family}})', fleet: 'Every PC' },
            scopeHint: {
                pc: 'Only this PC.',
                family: 'Every PC with {{family}} graphics.',
                fleet: 'Every PC at the venue.',
            },
            reason: 'Why',
            reasonHint: 'Kept in the record, for example “driver update broke games”.',
            reasonRequired: 'Say why you are wiping it.',
            confirm: { writeback: 'Wipe the changes', shaderCache: 'Wipe the shader cache' },
            sent: { writeback: 'Wipe sent to the boot server', shaderCache: 'Shader cache wipe sent to the boot server' },
            failed: 'The wipe could not be sent. Try again.',
        },
        seed: {
            title: 'Share this PC’s shader cache with every {{family}} PC?',
            body: 'PCs with {{family}} graphics then start with this PC’s warm shader cache instead of an empty one: new PCs at once, the others at their next boot.',
            offNote: 'Shut this PC down first, and every other PC with {{family}} graphics that has booted. The boot server refuses while any of them is on.',
            confirm: 'Share the shader cache',
            sent: 'Sent to the boot server',
            failed: 'The request could not be sent. Try again.',
        },
        restart: {
            title: 'Restart PCs that are behind on {{name}}?',
            body_one: '{{count}} PC is behind. It restarts onto the current version if nobody is playing on it.',
            body_other: '{{count}} PCs are behind. Each restarts onto the current version if nobody is playing on it.',
            skipNote: 'A PC with a session, a booking in the next 30 minutes, or an edit open is left alone.',
            confirm: 'Restart idle PCs',
            result: '{{restarted}} restarting, {{skipped}} left alone',
            failed: 'The PCs could not be restarted. Try again.',
        },
    },
    // -- On the Floor: the tile's strip, the station window's Windows & games, the Update waiting filter ----------
    floor: {
        // The tile's strip: how the PC starts, in a few words.
        state: {
            starting: 'Starting from the network',
            loading_windows: 'Loading Windows',
            ready: 'Ready',
            update_waiting: 'Update waiting',
            cant_start: 'Can’t start',
            never_started: 'Not started yet',
            // The boot server went quiet: its last word about the PC is not shown as if it were true.
            unknown: 'Boot status unknown',
        },
        unknownReason: 'The boot server has not reported for a while',
        unknownFix: 'Check the boot server is switched on and on the same network as the PCs. PCs already running keep running.',
        // A PC that has been starting or loading for a while says for how long: “Starting from the network · 2 min”.
        since: '{{words}} · {{duration}}',
        duration: { minutes: '{{count}} min', hours: '{{count}} h' },
        editing: 'Windows being changed',
        // Why a PC can't start, said plainly; the second line says what to do.
        problem: {
            no_boot_server: 'No boot server is paired',
            boot_server_offline: 'The boot server is offline',
            licence_expired: 'The Clavian Boot licence has run out',
            no_image: 'No Windows image is ready for it',
            other_motherboard: 'Its Windows image was made on another motherboard',
            start_failed: 'It stopped while starting',
        },
        fix: {
            no_boot_server: 'Pair the boot server in Clavian Boot.',
            boot_server_offline: 'Check the boot server is switched on and connected to the internet and the PCs’ network.',
            licence_expired: 'Connect the boot server to the internet so it can renew its licence, or ask Clavian about your add-on.',
            no_image: 'Finish an image in Clavian Boot, or give this PC one that is ready.',
            other_motherboard: 'Make an image on a PC with this motherboard, or move this PC to a class that has one.',
            start_failed: 'Restart it. If it stops again, check its network cable.',
        },
        // A game disk that could not take its drive letter because another drive of the PC holds it.
        letterBlocked: {
            title: 'A game disk could not take its letter',
            body: 'Games could not take {{letter}}: on this PC, because another drive of the PC uses it. Give the games a different letter.',
            change: 'Change the letter',
        },
        stripAria: 'Starts from the network: {{state}}',
        // The station window's tab.
        section: 'How it starts',
        facts: {
            windows: 'Windows',
            version: 'Version {{number}}',
            latest: 'Latest',
            updateWaiting: 'Update waiting · version {{number}} is ready',
            own: 'Set for this PC',
            // Where its Windows image (or its games) comes from.
            from: {
                pc: 'Set for this PC',
                class: 'From its class {{name}}',
                classUnnamed: 'From its class',
                venue: 'The venue’s default',
            },
            gamesFrom: {
                pc: 'Set for this PC',
                class: 'From its class {{name}}',
                classUnnamed: 'From its class',
                venue: 'The image’s games',
            },
            motherboard: 'Motherboard',
            noImage: 'No image',
            games: 'Games',
            noGames: 'No game disks',
            gameDisk: '{{name}} on {{letter}}:',
            gameDiskNoLetter: '{{name}}',
            lastStart: 'Last start',
            lastStartTook: '{{when}} · took {{seconds}} s',
            neverStarted: 'Not started from the boot server yet',
            secureBoot: 'Secure Boot',
            graphics: 'Graphics',
            changes: 'Changes kept on',
            unknown: 'Not reported',
            on: 'On',
            off: 'Off',
        },
        editingNotice: 'Windows is being changed on this PC. Save it or throw it away in PCs & games, under Windows images.',
        // An image works on the motherboard model it was made on.
        motherboard: {
            title: 'Another motherboard',
            mismatch: 'This PC’s motherboard ({{pc}}) is not the one this Windows image was made on ({{image}}). It may not start; make an image on this model.',
        },
        // Choosing this PC's own Windows image and games, or clearing them so its class's apply.
        choose: {
            open: 'Choose Windows for this PC',
            title: 'Windows and games for this PC',
            body: 'A choice here is this PC’s own and comes before its class’s. It takes effect at its next restart when nobody is playing.',
            image: 'Windows image',
            inheritClass: 'Use its class’s ({{name}})',
            inheritVenue: 'Use the venue’s default ({{name}})',
            inheritNone: 'Use its class’s or the venue’s default',
            games: 'Games',
            gamesInheritClass: 'Use its class’s',
            gamesInheritImage: 'Use the image’s games',
            gamesOwn: 'Choose for this PC',
            gamesPick: 'Game disks this PC sees',
            gamesRequired: 'Choose at least one game disk, or use the class’s.',
            save: 'Save',
            saved: 'Saved. The PC switches at its next restart when nobody is playing.',
            failed: 'The choice could not be saved. Try again.',
        },
        actions: {
            label: 'Windows actions',
            restart: 'Restart onto the latest when free',
            reset: 'Reset this PC’s changes',
            change: 'Change Windows using this PC',
        },
        restart: {
            title: 'Restart onto the latest when free?',
            body: 'This PC restarts onto version {{number}} of {{name}} as soon as nobody is playing on it. It is never restarted during a session, or with a booking in the next 30 minutes.',
            confirm: 'Restart when free',
            restarted: 'Restarting onto the latest now',
            left: {
                in_session: 'A session is running, so it was left alone. Try again once it ends.',
                reserved: 'It is booked in the next 30 minutes, so it was left alone.',
                editing: 'Windows is being changed on it, so it was left alone.',
                not_a_station: 'It is not on the floor, so it was left alone.',
            },
            nothing: 'It is already on the latest version.',
            failed: 'The PC could not be restarted. Try again.',
        },
        reset: {
            title: 'Reset this PC’s changes?',
            body: 'Everything changed on this PC since it started is thrown away. If it is running, it restarts onto a clean copy of Windows and its games.',
            reason: 'Why',
            reasonHint: 'Kept in the record, for example “a player installed something”.',
            reasonRequired: 'Say why you are resetting it.',
            confirm: 'Reset the changes',
            sent: 'Reset sent to the boot server',
            failed: 'The reset could not be sent. Try again.',
        },
        change: {
            title: 'Change Windows using this PC?',
            body: 'This PC restarts into {{name}} so you can install or update software on it. Every other PC keeps the current version until you save the change in PCs & games, under Windows images.',
            maintenance: 'It goes into maintenance while you work, so nobody can play on it.',
            busy: 'A session is running on this PC. Change Windows on a PC nobody is using.',
            confirm: 'Start changing Windows',
            started: 'This PC is restarting into Windows to be changed',
            failed: 'Changing Windows could not start. Try again.',
        },
        advanced: {
            summary: 'Advanced',
            writeback: 'Where its changes are kept',
            writebackHint: 'While the PC runs, what it writes is kept until its next start (its write-back). It applies from its next start.',
            saveWriteback: 'Save',
            writebackSaved: 'Saved',
            writebackFailed: 'The choice could not be saved. Try again.',
            seed: 'Share its shader cache',
            seedHint: 'PCs with {{family}} graphics then start with this PC’s warm shader cache. Shut this PC and the other {{family}} PCs down first.',
            seedConfirm: 'Share the shader cache',
            clearShader: 'Clear shader caches',
        },
        // The Update waiting filter on the Floor.
        updates: {
            summary_one: '{{count}} PC has an update waiting. It gets the latest Windows the next time it restarts.',
            summary_other: '{{count}} PCs have an update waiting. They get the latest Windows the next time they restart.',
            restartAll: 'Restart all when free',
            title: 'Restart every PC with an update waiting?',
            body: 'Each one restarts onto the latest Windows as soon as nobody is playing on it. A PC with a session, a booking in the next 30 minutes, or a change of Windows open is left alone.',
            confirm: 'Restart when free',
            result: '{{restarted}} restarting, {{skipped}} left alone',
            failed: 'The PCs could not be restarted. Try again.',
        },
    },
    // -- The Floor's New PCs tray: a PC that started from the boot server and is not a station yet ----------------
    newPcs: {
        title: 'New PCs',
        subtitle: 'These PCs started from the boot server and are not on the floor yet.',
        subtitleBoth: 'Started from Clavian Boot, and not a station yet.',
        firstSeen: 'First seen {{when}}',
        lastStart: 'last start {{when}}',
        neverStarted: 'not started yet',
        noHardware: 'Hardware not reported yet',
        cardAria: 'New PC {{mac}}',
        identify: 'Identify',
        identifyHint: 'Shows the number on the PC’s own screen for a minute. A PC that is off, or still starting, cannot show it.',
        showing: 'Showing {{label}} on its screen',
        identifyOffline: 'The boot server is offline, so the PC cannot be asked to show its number.',
        identifyFailed: 'The number could not be sent to the PC. Try again.',
        make: 'Make it station {{number}}',
        unavailable: 'The new PCs could not be loaded.',
        details: 'Details',
        mac: 'Network address',
        motherboard: 'Motherboard {{name}}',
        collapsed_one: '{{count}} new PC',
        collapsed_other: '{{count}} new PCs',
        setUp: 'Set up',
        hide: 'Hide',
        remove: 'Remove…',
        removeTitle: 'Remove this PC?',
        removeBody: 'It leaves the New PCs list and stops counting against your Clavian Boot PCs. It comes back as a new PC if it starts from the boot server again while “Add new PCs automatically” is on in your settings.',
        removeConfirm: 'Remove PC',
        removed: 'PC removed',
        removeFailed: 'The PC could not be removed. Try again.',
    },
    // -- Make it station: the new PC becomes a station on the Floor --------------------------------------------
    makeStation: {
        title: 'Make it a station',
        pairs: 'It goes on the floor with a tile of its own. The Clavian Station app on this PC pairs to it by itself, so there is no code to type.',
        atOnce: 'It goes on the floor with a tile of its own, as soon as you make it.',
        number: 'Station number',
        numberHint: 'The next free number. It is the number on the tile.',
        class: 'Class',
        chooseClass: 'Choose a class',
        noClasses: 'This venue has no station class yet. Make one under Stations, then come back.',
        zone: 'Zone',
        zoneNone: 'No zone',
        more: 'More options',
        name: 'Name',
        nameHint: 'A tile shows its number. A name goes beside it, like “Corner rig”.',
        submit: 'Make it station {{number}}',
        numberRequired: 'Enter a station number from 1 to {{max}}.',
        classRequired: 'Choose a class.',
        numberTaken: 'Another station already has number {{number}}.',
        notFound: 'That class or zone is no longer there. Choose again.',
        alreadyStation: 'This PC is already a station.',
        failed: 'The station could not be made. Try again.',
        madePairing: 'Station {{number}} is on the floor. The Clavian Station app on that PC pairs by itself.',
        made: 'Station {{number}} is on the floor',
    },
    // -- A PC's hardware in words ---------------------------------------------------------------------------------
    hardware: {
        gpu: {
            vendor: { nvidia: 'NVIDIA', amd: 'AMD', intel: 'Intel' },
            withDriver: '{{vendor}} · driver {{driver}}',
        },
        nic: {
            intel: 'Intel',
            realtek: 'Realtek',
            killer: 'Killer',
            broadcom: 'Broadcom',
            marvell: 'Marvell',
            mellanox: 'Mellanox',
            other: 'Network card',
        },
    },
    // -- The Floor of PCs: a venue with Clavian Boot and no Clavian Stations ---------------------------------------
    bootOnly: {
        intro_one: '{{count}} PC on the floor, started from the boot server.',
        intro_other: '{{count}} PCs on the floor, started from the boot server.',
        groupCount: '{{ready}} ready of {{total}}',
        emptyTitle: 'No PCs on the floor yet',
        empty: 'A PC that starts from your boot server for the first time appears under New PCs. Make it a station to put it on the floor.',
        filter: { ready: 'Ready', cantStart: 'Can’t start', offline: 'Offline' },
        filterEmpty: {
            ready: 'No PC is ready right now',
            cantStart: 'Every PC can start',
            offline: 'The boot server is online',
        },
        figures: {
            label: 'Your PCs at a glance',
            ready: 'Ready',
            ofTotal: 'of {{total}} PCs',
            readyHint: '{{percent}}% can start right now',
            update: 'Update waiting',
            updateHint: 'They get the latest Windows on their next restart',
            cantStart: 'Can’t start',
            cantStartHint: 'Open a PC to see why',
        },
        tile: {
            noPc: 'No PC linked yet',
            waitingForApp: 'Waiting for the Clavian Station app',
        },
        search: {
            label: 'Find a PC',
            placeholder: 'Search PCs',
            empty: 'No PC matches “{{query}}”.',
        },
        needs: {
            clear: 'Every PC is ready.',
            cantStart: 'Can’t start',
            updateWaiting: 'Update waiting',
            unknown: 'Not reporting',
        },
    },
    // -- What a station class's PCs start (its Edit dialog's Windows & games tab) ----------------------------
    classes: {
        tab: 'Windows & games',
        intro: 'What this class’s PCs start from the boot server. A PC with its own choice, set in its window on the Floor, keeps it.',
        image: 'Windows image',
        imageDefault: 'The venue’s default ({{name}})',
        imageDefaultNone: 'The venue’s default',
        games: 'Games',
        gamesDefault: 'The image’s own games',
        gamesChoose: 'Choose game disks for this class',
        gamesPick: 'Game disks this class’s PCs see',
        gamesRequired: 'Choose at least one game disk, or use the image’s own.',
        noImages: 'No Windows image is ready yet. Make one in Clavian Boot.',
        save: 'Save',
        saved: 'Saved. Its PCs switch at their next restart when nobody is playing.',
        failed: 'The choice could not be saved. Try again.',
        readOnly: 'Only a member who manages stations and Clavian Boot can change this.',
        cardWindows: 'Windows',
    },
    // -- Health --------------------------------------------------------------------------------------
    health: {
        title: 'How your PCs are doing',
        unavailable: 'The health figures could not be loaded.',
        empty: 'No health figures yet. They appear once PCs have booted from the boot server.',
        slow: 'Slow',
        low: 'Low',
        range: { label: 'Time range', hour: 'Last hour', day: 'Last 24 hours', week: 'Last 7 days', month: 'Last 30 days' },
        stats: {
            uplink: 'Server network use',
            queue: 'Server disk queue',
            slowest: 'Slowest PC',
            lowestHit: 'Lowest cache hits',
        },
        uplinkOk: 'Latest reading. Comfortable.',
        uplinkHot: 'Over {{limit}}%. PCs may boot slowly. Wait, or move some PCs to another network port.',
        queueHint: 'Requests waiting for the disk, latest reading.',
        slowHint: '{{pc}} reads slower than {{limit}} ms at its worst.',
        lowHint: '{{pc}} finds less than {{limit}}% of what it reads in the cache.',
        uplinkChart: {
            title: 'Boot server network use',
            intro: 'How much of the boot server’s network link is in use. Above {{limit}}% is flagged.',
            summary: 'Network use peaked at {{peak}}.',
        },
        latencyChart: {
            title: 'Read delay per PC',
            intro: 'The slowest 1% of reads, at the worst moment in the range. Over {{limit}} ms is flagged.',
        },
        hitChart: {
            title: 'Cache hits per PC',
            intro: 'How much of what a PC reads is already in memory. Under {{limit}}% is flagged.',
        },
        bootChart: {
            title: 'Last boot time per PC',
            intro: 'From the PC starting to the lock screen showing.',
        },
        table: 'Health figures per PC',
        columns: { pc: 'PC', p99: 'Slowest 1% of reads', hit: 'Cache hits' },
    },
    // -- Settings ------------------------------------------------------------------------------------
    settings: {
        title: 'Boot server settings',
        intro: 'How the boot server answers PCs, where a PC keeps its changes and how the caches work. These are the same settings as in Settings → Clavian Boot.',
        unavailable: 'The settings could not be loaded.',
    },
};
/** The organisation's add-ons page, read-only: what it pays for each month. */
export const addonsCopy = {
    title: 'Add-ons',
    intro: 'The extras Clavian has added to your organisation, and what they cost each month.',
    loading: 'Loading add-ons',
    unavailable: 'Your add-ons could not be loaded.',
    notPermitted: 'Only members who manage billing can see add-ons.',
    empty: {
        title: 'No add-ons',
        body: 'Add-ons such as Clavian Boot appear here once Clavian adds them to your organisation.',
    },
    summary: 'Monthly total',
    totalLabel: 'You pay each month:',
    active: 'Active add-ons',
    noneActive: 'No add-on is active.',
    ended: 'Ended',
    cancelled: 'Ended',
    perPcMonth: '{{price}} per PC a month',
    columns: { name: 'Add-on', pcs: 'PCs', unitPrice: 'Price', monthly: 'Each month', since: 'Since', status: 'State', until: 'Ended on' },
    footer: 'The number of PCs is what your boot server may boot. To change it or end an add-on, ask Clavian.',
};
//# sourceMappingURL=boot.js.map